/**
 * iter102 · Driving licence + car-rental reference block.
 *
 * iter103 (Priority 2) redesign:
 *   Original was two big side-by-side cards. Collapsed to a single compact
 *   band that shows only the load-bearing signals by default:
 *     · one IDP badge (legal status)
 *     · one line of license validity or the "confirming" copy for
 *       needs_verification states
 *     · five brand logo/text chips that link out to live booking pages
 *   Everything else (universal rental facts, exception notes, last-verified
 *   footer, "informational only" disclaimer) collapses behind a chevron.
 *
 *   Rules for this pass:
 *     · No em dash in any UI copy.
 *     · last_verified always renders as "Aug 26, 2026".
 *     · Rental brands link out to real live booking domains, no affiliate
 *       codes, plain <a href> with target=_blank + rel=noopener.
 */
import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, IdCard, Info, ShieldCheck } from "lucide-react";

const RENTAL_BRANDS = [
  { name: "Europcar", url: "https://www.europcar.com" },
  { name: "Hertz",    url: "https://www.hertz.com" },
  { name: "Sixt",     url: "https://www.sixt.com" },
  { name: "Avis",     url: "https://www.avis.com" },
  { name: "Budget",   url: "https://www.budget.com" },
];

const RENTAL_ONE_LINER =
  "Credit card in the renter's own name is usually required (debit or prepaid forex often refused), age surcharge is typical between 21 and 25.";

const RENTAL_IDP_NUANCE =
  "Counters mostly ask for an IDP only when the licence is not in Latin script. Indian licences are, in English, so a counter may not ask. That does NOT override the country's legal requirement above.";

const LAST_UPDATED = "Aug 26, 2026";

/* ------------------------------------------------------------------------ */

function StatusPill({ status, enforcement }) {
  const base =
    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.14em] border";
  if (status === "not_required") {
    return (
      <span
        data-testid="driving-badge"
        data-status="not_required"
        className={`${base} bg-teal-500/12 text-teal-700 border-teal-500/25`}
      >
        <ShieldCheck size={11} strokeWidth={2.4} /> IDP not required
      </span>
    );
  }
  if (status === "needs_verification") {
    return (
      <span
        data-testid="driving-badge"
        data-status="needs_verification"
        className={`${base} bg-ink/[0.06] text-ink/70 border-ink/20`}
      >
        <Info size={11} strokeWidth={2.4} /> Verify locally
      </span>
    );
  }
  if (status === "required") {
    const strict = enforcement === "strict";
    return (
      <span
        data-testid="driving-badge"
        data-status="required"
        data-enforcement={enforcement || "routine"}
        className={
          base +
          " " +
          (strict
            ? "bg-red-500/12 text-red-700 border-red-500/25"
            : "bg-amber-500/15 text-amber-800 border-amber-500/35")
        }
      >
        <AlertTriangle size={11} strokeWidth={2.4} /> IDP required{strict ? " · strict" : ""}
      </span>
    );
  }
  return (
    <span
      data-testid="driving-badge"
      data-status={status || "unknown"}
      className={`${base} bg-ink/[0.06] text-ink/70 border-ink/20`}
    >
      <Info size={11} strokeWidth={2.4} /> Check locally
    </span>
  );
}

function BrandChips() {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="rental-majors">
      {RENTAL_BRANDS.map((b) => (
        <a
          key={b.name}
          href={b.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`rental-brand-${b.name.toLowerCase()}`}
          className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold text-ink/80 bg-white border border-ink/15 hover:border-elec hover:text-elec transition-colors"
        >
          {b.name}
        </a>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------------ */

export default function DrivingCard({ driving, countryName }) {
  const [expanded, setExpanded] = useState(false);
  if (!driving || !driving.idp_status) return null;

  const isVerify = driving.idp_status === "needs_verification";

  return (
    <section
      className="mt-8 rounded-2xl border border-ink/10 bg-white overflow-hidden"
      data-testid="driving-card"
      data-country={countryName}
      aria-label={`Driving in ${countryName}`}
    >
      {/* ---------- Compact top row. Everything load-bearing lives here. ---------- */}
      <div className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="w-8 h-8 rounded-full bg-elec/10 text-elec flex items-center justify-center">
            <IdCard size={16} strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">
              Driving in {countryName}
            </div>
            <div className="mt-0.5">
              <StatusPill status={driving.idp_status} enforcement={driving.enforcement_level} />
            </div>
          </div>
        </div>

        <div
          className="flex-1 min-w-0 text-[13.5px] font-semibold text-ink/90 leading-snug"
          data-testid="driving-license-validity"
        >
          {isVerify
            ? <>Rules conflict between our sources. Confirm with a local host university officer or the {countryName} embassy before relying on this.</>
            : driving.license_validity}
        </div>

        <div className="shrink-0">
          <BrandChips />
        </div>

        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          data-testid="driving-toggle"
          aria-expanded={expanded}
          className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold text-elec hover:text-elec/80"
        >
          {expanded ? <>Less <ChevronUp size={12} /></> : <>More <ChevronDown size={12} /></>}
        </button>
      </div>

      {/* ---------- Progressive disclosure. Universal rental facts, exception ---------- */}
      {/*             notes, disclaimer, last-verified stamp. */}
      {expanded && (
        <div
          className="border-t border-ink/10 px-4 md:px-5 py-4 space-y-3 bg-ink/[0.02]"
          data-testid="driving-details"
        >
          {driving.note && !isVerify && (
            <div
              className="text-[12.5px] text-ink/80 leading-snug"
              data-testid="driving-note"
            >
              <span className="uppercase tracking-widest text-[10px] font-bold text-elec mr-1.5">Note</span>
              {driving.note}
            </div>
          )}

          <div className="text-[12.5px] text-ink/80 leading-snug" data-testid="rental-facts">
            <span className="uppercase tracking-widest text-[10px] font-bold text-elec mr-1.5">
              Renting a car
            </span>
            {RENTAL_ONE_LINER}
          </div>

          <div className="text-[12px] text-ink/70 leading-snug">
            {RENTAL_IDP_NUANCE}
          </div>

          <div className="pt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted">
            <span data-testid="driving-last-verified">Last updated {LAST_UPDATED}</span>
            <span>Brand links go to public booking sites. No affiliate.</span>
          </div>
        </div>
      )}
    </section>
  );
}
